import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, LogOut, Phone, CalendarClock, DollarSign, Inbox, Star, X as XIcon, FileText, Send, Link2 } from "lucide-react";
import { toast } from "sonner";
import { LogoMark } from "../components/Logo";
import { adminLogin, adminList, adminUpdate, adminStats, adminCreateInvoice } from "../lib/api";
import { frequencyLabel, planLabel, ADDONS } from "../lib/constants";

const TOKEN_KEY = "ews_admin_token";
const STATUS_META = {
  awaiting_payment: { label: "Awaiting payment", cls: "text-amber-300 border-amber-400/40 bg-amber-400/10" },
  paid: { label: "Paid", cls: "text-crystal border-crystal/40 bg-crystal/10" },
  completed: { label: "Completed", cls: "text-emerald-300 border-emerald-400/40 bg-emerald-400/10" },
  cancelled: { label: "Cancelled", cls: "text-red-300 border-red-400/40 bg-red-400/10" },
};
const FILTERS = ["all", "awaiting_payment", "paid", "completed"];
const inputCls =
  "w-full rounded-xl bg-ink/80 border border-white/15 px-4 py-3 text-sm text-white placeholder:text-mist/60 outline-none focus:border-crystal focus:ring-1 focus:ring-crystal/50 transition-colors duration-200";

function LoginScreen({ onToken }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { token } = await adminLogin(password);
      sessionStorage.setItem(TOKEN_KEY, token);
      onToken(token);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed — try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-midnight flex items-center justify-center px-5">
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-sm rounded-3xl glass-panel p-8"
        data-testid="admin-login-card"
      >
        <div className="flex items-center gap-3 mb-8">
          <LogoMark size={38} />
          <div>
            <div className="font-display text-xl text-white leading-none">Everything Works</div>
            <div className="font-mono text-[9px] tracking-[0.3em] uppercase text-crystal mt-1.5">Owner Dashboard</div>
          </div>
        </div>
        <label className="font-mono text-[11px] tracking-[0.25em] uppercase text-mist block mb-3">Owner password</label>
        <input
          data-testid="admin-login-input"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••••"
          className="w-full rounded-xl bg-ink/80 border border-white/15 px-4 py-3.5 text-sm text-white outline-none focus:border-crystal focus:ring-1 focus:ring-crystal/50 transition-colors duration-200"
          autoFocus
        />
        {error && <p className="mt-2 text-xs text-red-400" data-testid="admin-login-error">{error}</p>}
        <button
          data-testid="admin-login-btn"
          type="submit"
          disabled={busy}
          className={`mt-6 w-full flex items-center justify-center gap-2 rounded-full py-4 font-mono text-[11px] font-semibold tracking-[0.25em] uppercase transition-all duration-200 ${
            busy ? "bg-white/10 text-mist cursor-wait" : "bg-crystal text-midnight hover:-translate-y-0.5"
          }`}
        >
          <Lock size={13} /> {busy ? "Checking…" : "Open dashboard"}
        </button>
      </motion.form>
    </div>
  );
}

function InvoiceModal({ booking, token, onClose, onSent }) {
  const isBlank = !booking;
  const glass = booking ? booking.total - (booking.addons_total || 0) : 0;
  const [f, setF] = useState(() => booking
    ? {
        customer_name: booking.name,
        customer_phone: booking.phone,
        customer_email: booking.email || "",
        address: booking.address,
        date: booking.date,
        time_slot: booking.time_slot,
        job_type: booking.service_key === "storefront" ? "store" : "house",
        scope_text: [
          `${booking.scope === "exterior" ? "Exterior only" : "Inside & out"} window cleaning — ${booking.count} ${booking.unit}s`,
          "Squeegee-detailed pane by pane, edges hand-finished",
          ...Object.entries(booking.addons || {}).filter(([, v]) => v > 0)
            .map(([k, v]) => `${(ADDONS.find((a) => a.key === k)?.label) || k} — ${v}`),
        ].join("\n"),
        customer_email_send: booking.email || "",
      }
    : {
        customer_name: "", customer_phone: "", customer_email: "", address: "",
        date: new Date().toISOString().slice(0, 10), time_slot: "9:00 AM",
        job_type: "house", scope_text: "", customer_email_send: "",
      });

  const items = booking
    ? [
        { label: `${booking.service_label} — ${booking.count} ${booking.unit}(s) · ${booking.scope === "exterior" ? "Exterior only" : "Inside & out"}`, amount: glass },
        ...Object.entries(booking.addons || {})
          .filter(([, v]) => v > 0)
          .map(([k, v]) => {
            const a = ADDONS.find((x) => x.key === k);
            return { label: a ? `${a.label} × ${v}` : `${k} × ${v}`, amount: (a?.price || 0) * v };
          }),
      ]
    : [{ label: "Window cleaning — describe in the scope below", amount: 0 }];

  const total = items.reduce((s, i) => s + i.amount, 0);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);

  const send = async () => {
    if (!f.customer_name.trim() || !f.address.trim()) {
      toast.error("Customer name and address are required");
      return;
    }
    if (f.customer_email_send && !/^\S+@\S+\.\S+$/.test(f.customer_email_send)) {
      toast.error("Enter a valid customer email, or uncheck sending");
      return;
    }
    setSending(true);
    try {
      const inv = await adminCreateInvoice(token, {
        booking_ref: booking?.ref || undefined,
        customer_name: f.customer_name,
        customer_phone: f.customer_phone,
        customer_email: f.customer_email_send || f.customer_email || undefined,
        address: f.address,
        date: f.date,
        time_slot: f.time_slot,
        job_type: f.job_type,
        scope_lines: f.scope_text.split("\n").filter((l) => l.trim()),
        items,
        send_email: !!f.customer_email_send,
      });
      setSent(inv);
      onSent(inv);
      toast.success(`Invoice ${inv.ref} created${f.customer_email_send ? " and emailed" : ""}`);
    } catch (err) {
      toast.error("Could not create the invoice — try again");
    } finally {
      setSending(false);
    }
  };

  return (
    <motion.div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="invoice-modal">
      <div className="absolute inset-0 bg-midnight/85 backdrop-blur-md" onClick={onClose} />
      <motion.div
        initial={{ y: 60, opacity: 0, scale: 0.98 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: 40, opacity: 0, scale: 0.98 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full sm:max-w-xl max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl border border-white/15 bg-[#0B0E14] p-6 sm:p-8"
        style={{ boxShadow: "0 60px 140px -40px rgba(0,0,0,1)" }}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-3xl text-white flex items-center gap-3">
            <FileText size={22} className="text-crystal" /> Customer invoice
          </h3>
          <button data-testid="invoice-modal-close" onClick={onClose} className="rounded-full border border-white/15 p-2 text-mist hover:text-white hover:border-crystal transition-colors duration-200" aria-label="Close">
            <XIcon size={16} />
          </button>
        </div>

        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <input data-testid="invoice-customer-name" placeholder="Customer name *" value={f.customer_name} onChange={(e) => setF({ ...f, customer_name: e.target.value })} className={inputCls} />
            <input data-testid="invoice-customer-phone" placeholder="Customer phone" value={f.customer_phone} onChange={(e) => setF({ ...f, customer_phone: e.target.value })} className={inputCls} />
          </div>
          <input data-testid="invoice-address" placeholder="Service address *" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} className={inputCls} />
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-mist mb-1.5">When I'll be there</div>
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="invoice-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={inputCls} />
                <input data-testid="invoice-time" placeholder="e.g. 10:00 AM" value={f.time_slot} onChange={(e) => setF({ ...f, time_slot: e.target.value })} className={inputCls} />
              </div>
            </div>
            <div>
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-mist mb-1.5">Job type</div>
              <div className="grid grid-cols-2 gap-3">
                {[["house", "House"], ["store", "Store"]].map(([v, l]) => (
                  <button key={v} data-testid={`invoice-jobtype-${v}`} onClick={() => setF({ ...f, job_type: v })} className={`rounded-xl border py-3 font-mono text-[10px] tracking-[0.2em] uppercase transition-colors duration-200 ${f.job_type === v ? "border-crystal bg-crystal/10 text-crystal" : "border-white/15 text-mist hover:text-white"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-mist mb-1.5">What I'll be doing (one per line)</div>
            <textarea data-testid="invoice-scope" rows={4} value={f.scope_text} onChange={(e) => setF({ ...f, scope_text: e.target.value })} className={`${inputCls} resize-none`} placeholder={"Inside & out window cleaning — 12 panes\nScreens washed both sides"} />
          </div>

          <div className="rounded-xl border border-white/10 bg-ink/50 overflow-hidden" data-testid="invoice-items-preview">
            {items.map((it, i) => (
              <div key={i} className="flex items-center justify-between gap-4 px-4 py-3 border-b border-white/10">
                <span className="text-sm text-white/90">{it.label}</span>
                <span className="font-mono text-sm text-white">${it.amount}</span>
              </div>
            ))}
            <div className="flex items-center justify-between gap-4 px-4 py-3.5 bg-crystal/[0.07]">
              <span className="font-display text-xl text-white">Total</span>
              <span className="font-display text-3xl text-crystal" data-testid="invoice-modal-total">${total}</span>
            </div>
          </div>

          <div className="rounded-xl border border-white/15 bg-ink/60 px-4 py-3.5">
            <label className="flex items-center gap-3 cursor-pointer">
              <input data-testid="invoice-send-email-check" type="checkbox" checked={!!f.customer_email_send} onChange={(e) => setF({ ...f, customer_email_send: e.target.checked ? (f.customer_email || f.customer_email_send || "") : "" })} className="w-4 h-4 accent-[#00E5FF]" />
              <span className="text-sm text-white/90">Email this invoice to the customer</span>
            </label>
            {f.customer_email_send !== "" && (
              <input data-testid="invoice-customer-email" placeholder="Customer email *" value={f.customer_email_send} onChange={(e) => setF({ ...f, customer_email_send: e.target.value })} className={`${inputCls} mt-3`} />
            )}
            <p className="text-mist/70 text-[11px] mt-2">They get the time, the scope of work, line items, total, and your Zelle details with the invoice number as the memo.</p>
          </div>

          <button
            data-testid="invoice-send-btn"
            onClick={send}
            disabled={sending}
            className={`w-full flex items-center justify-center gap-2 rounded-full py-4 font-mono text-[11px] font-semibold tracking-[0.25em] uppercase transition-all duration-200 ${
              sending ? "bg-white/10 text-mist cursor-wait" : "bg-crystal text-midnight hover:-translate-y-0.5"
            }`}
          >
            <Send size={13} /> {sending ? "Sending…" : "Create invoice" + (f.customer_email_send ? " & email it" : "")}
          </button>

          {sent && (
            <div className="rounded-xl border border-crystal/30 bg-crystal/[0.06] px-4 py-3.5" data-testid="invoice-link-box">
              <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase text-crystal mb-1.5">
                <Link2 size={12} /> Shareable link — text this to the customer
              </div>
              <div className="font-mono text-xs text-white break-all">
                {window.location.origin}/invoice/{sent.ref}
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [invBooking, setInvBooking] = useState(undefined); // undefined = closed, null = blank invoice

  const load = useCallback(async (t = token, f = filter) => {
    if (!t) return;
    setLoading(true);
    try {
      const [list, s] = await Promise.all([adminList(t, f), adminStats(t)]);
      setBookings(list);
      setStats(s);
    } catch (err) {
      if (err?.response?.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken(null);
        toast.error("Session expired — log in again");
      } else {
        toast.error("Could not load bookings");
      }
    } finally {
      setLoading(false);
    }
  }, [token, filter]);

  useEffect(() => { load(); }, []); // eslint-disable-line

  const setStatus = async (ref, status) => {
    try {
      await adminUpdate(token, ref, status);
      toast.success(`${ref} → ${STATUS_META[status].label}`);
      load();
    } catch {
      toast.error("Update failed — try again");
    }
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (!token) return <LoginScreen onToken={setToken} />;

  return (
    <div className="min-h-screen bg-midnight text-white font-body">
      <header className="sticky top-0 z-40 bg-midnight/60 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 h-[68px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogoMark size={30} />
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-mist">Owner Dashboard</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="/team" data-testid="admin-team-link" className="hidden sm:inline-flex font-mono text-[10px] tracking-[0.2em] uppercase text-mist hover:text-crystal transition-colors duration-200">
              Crew schedule
            </a>
            <a href="/" data-testid="admin-view-site-link" className="hidden sm:inline-flex font-mono text-[10px] tracking-[0.2em] uppercase text-mist hover:text-crystal transition-colors duration-200">
              View site
            </a>
            <button data-testid="admin-new-invoice-btn" onClick={() => setInvBooking(null)} className="flex items-center gap-2 rounded-full border border-crystal/50 px-4 py-2 font-mono text-[10px] tracking-[0.2em] uppercase text-crystal hover:bg-crystal hover:text-midnight transition-colors duration-200">
              <FileText size={12} /> New invoice
            </button>
            <button data-testid="admin-logout-btn" onClick={logout} className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 font-mono text-[10px] tracking-[0.2em] uppercase text-mist hover:text-white hover:border-crystal transition-colors duration-200">
              <LogOut size={12} /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-5 sm:px-8 py-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {[
            { icon: CalendarClock, label: "Upcoming jobs", value: stats?.upcoming ?? "—", testid: "admin-stat-upcoming" },
            { icon: Inbox, label: "Awaiting payment", value: stats?.awaiting_payment ?? "—", testid: "admin-stat-awaiting" },
            { icon: DollarSign, label: "Collected", value: stats ? `$${stats.paid_revenue}` : "—", testid: "admin-stat-revenue" },
            { icon: Star, label: "Total bookings", value: stats?.total_bookings ?? "—", testid: "admin-stat-total" },
          ].map((s) => (
            <div key={s.label} className="rounded-2xl glass-panel p-5" data-testid={s.testid}>
              <s.icon size={16} className="text-crystal mb-3" />
              <div className="font-display text-3xl leading-none">{s.value}</div>
              <div className="font-mono text-[9px] tracking-[0.25em] uppercase text-mist mt-2">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="flex gap-2 mb-6 flex-wrap" data-testid="admin-filters">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`admin-filter-${f}`}
              onClick={() => { setFilter(f); load(token, f); }}
              className={`rounded-full px-5 py-2.5 font-mono text-[10px] tracking-[0.2em] uppercase border transition-colors duration-200 ${
                filter === f ? "bg-crystal text-midnight border-crystal font-semibold" : "border-white/15 text-mist hover:text-white"
              }`}
            >
              {f === "all" ? "All" : STATUS_META[f].label}
            </button>
          ))}
        </div>

        <div className="space-y-4" data-testid="admin-booking-list">
          {loading && <p className="font-mono text-xs text-mist">Loading…</p>}
          {!loading && bookings.length === 0 && (
            <p className="font-mono text-xs text-mist" data-testid="admin-empty">No bookings here yet.</p>
          )}
          {bookings.map((b) => (
            <motion.div
              key={b.ref}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="rounded-2xl glass-panel p-5 sm:p-6 grid md:grid-cols-[1fr_1fr_1fr_auto] gap-5 items-start"
              data-testid={`admin-booking-row-${b.ref}`}
            >
              <div>
                <div className="font-mono text-xs text-crystal">{b.ref}</div>
                <div className="font-display text-2xl text-white mt-1">{b.name}</div>
                <a href={`tel:${b.phone}`} data-testid={`admin-call-${b.ref}`} className="font-mono text-[11px] text-mist hover:text-crystal transition-colors duration-200">{b.phone}</a>
                <div className="text-xs text-mist mt-2">{b.address}</div>
              </div>
              <div className="font-mono text-[11px] text-mist space-y-1.5">
                <div>{b.service_label}</div>
                <div className="text-white/80">{b.count} {b.unit}(s) · {b.scope === "exterior" ? "Exterior only" : "Inside & out"}</div>
                {b.frequency !== "one_time" && <div className="text-crystal">{planLabel(b.frequency, b.plan_months)}</div>}
                {(b.addons_total || 0) > 0 && (
                  <div className="text-crystal" data-testid={`admin-addons-${b.ref}`}>
                    +${b.addons_total} around the glass —{" "}
                    {Object.entries(b.addons || {}).filter(([, v]) => v > 0).map(([k, v]) => `${v}× ${k}`).join(", ")}
                  </div>
                )}
                {b.notes && <div className="text-mist/70 italic">"{b.notes}"</div>}
              </div>
              <div className="font-mono text-[11px] text-mist space-y-1.5">
                <div className="text-white/90 text-sm">{b.date} · {b.time_slot}</div>
                <div className="font-display text-3xl text-white" data-testid={`admin-total-${b.ref}`}>${b.total}</div>
                <span className={`inline-block rounded-full border px-3 py-1 text-[9px] tracking-[0.2em] uppercase ${STATUS_META[b.status].cls}`} data-testid={`admin-status-${b.ref}`}>
                  {STATUS_META[b.status].label}
                </span>
              </div>
              <div className="flex md:flex-col gap-2 md:items-end flex-wrap justify-end">
                <button
                  data-testid={`admin-invoice-btn-${b.ref}`}
                  onClick={() => setInvBooking(b)}
                  className="rounded-full border border-crystal/50 px-4 py-2.5 font-mono text-[10px] tracking-[0.15em] uppercase text-crystal hover:bg-crystal hover:text-midnight transition-colors duration-200"
                >
                  <FileText size={11} className="inline mr-1.5" /> Invoice
                </button>
                {b.status === "awaiting_payment" && (
                  <button data-testid={`admin-mark-paid-${b.ref}`} onClick={() => setStatus(b.ref, "paid")} className="rounded-full bg-crystal px-5 py-2.5 font-mono text-[10px] font-semibold tracking-[0.15em] uppercase text-midnight transition-transform duration-200 hover:-translate-y-0.5">
                    Mark paid
                  </button>
                )}
                {b.status === "paid" && (
                  <button data-testid={`admin-mark-done-${b.ref}`} onClick={() => setStatus(b.ref, "completed")} className="rounded-full border border-emerald-400/50 px-5 py-2.5 font-mono text-[10px] tracking-[0.15em] uppercase text-emerald-300 hover:bg-emerald-400/10 transition-colors duration-200">
                    Mark done
                  </button>
                )}
                {(b.status === "awaiting_payment" || b.status === "paid") && (
                  <button data-testid={`admin-cancel-${b.ref}`} onClick={() => setStatus(b.ref, "cancelled")} className="rounded-full border border-white/15 p-2.5 text-mist hover:text-red-300 hover:border-red-400/50 transition-colors duration-200" aria-label="Cancel booking">
                    <XIcon size={12} />
                  </button>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        <p className="mt-10 font-mono text-[10px] tracking-[0.2em] uppercase text-mist/60 flex items-center gap-2">
          <Phone size={11} /> Alerts: booking emails go to ahmadmalexa@gmail.com · texts to 630-909-3759 once Twilio keys are added
        </p>
      </main>

      <AnimatePresence>
        {invBooking !== undefined && (
          <InvoiceModal
            booking={invBooking}
            token={token}
            onClose={() => setInvBooking(undefined)}
            onSent={() => {}}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
